import {
  Box,
  Paper,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
    scale: 0.98,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
  exit: (direction) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
    scale: 0.98,
  }),
};

export const RecentActivities = ({ activities }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsPerPage = isMobile ? 1 : 2;

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const maxIndex = Math.max(0, activities.length - itemsPerPage);
  const canGoNext = current < maxIndex;
  const canGoPrev = current > 0;

  const handleNext = () => {
    if (canGoNext) {
      setDirection(1);
      setCurrent((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (canGoPrev) {
      setDirection(-1);
      setCurrent((prev) => prev - 1);
    }
  };

  const visibleRows = activities.slice(current, current + itemsPerPage);
  const motionKey = `${current}-${itemsPerPage}`;

  return (
    <Box width="100%" position="relative">
      {canGoPrev && (
        <IconButton
          onClick={handlePrev}
          sx={{
            position: "absolute",
            left: -33,
            top: "40%",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
          aria-label="Anterior"
        >
          <ArrowBackIosNewIcon />
        </IconButton>
      )}

      <Box sx={{ width: "100%" }}>
        <Box
          minHeight={170}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          position="relative"
          width="100%"
          overflow="hidden"
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            gap={2}
            overflow="hidden"
          >
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={motionKey}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35 }}
                style={{
                  display: "flex",
                  gap: "16px",
                  width: "100%",
                  position: "absolute",
                  padding: "0 5px",
                  height: "90%",
                }}
              >
                {visibleRows.map((row, index) => (
                  <Paper
                    component={"button"}
                    elevation={3}
                    key={`${index}`}
                    sx={{
                      flex: visibleRows.length === 1 ? "auto" : 1,
                      maxWidth: visibleRows.length === 1 ? "26rem" : "100%",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "none",
                      height: "100%",
                      "&:hover": {
                        opacity: 0.6,
                      },
                      cursor: "pointer",
                    }}
                    onClick={row.onClick}
                  >
                    <Box width="100%" padding={2}>
                      <Typography
                        fontWeight="bold"
                        fontSize={18}
                        textAlign="start"
                      >
                        {row?.title}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        fontSize={16}
                        mt={1}
                        textAlign="start"
                      >
                        {row?.date}
                      </Typography>
                    </Box>
                    {row.icon && row.icon}
                  </Paper>
                ))}
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Box>

      {canGoNext && (
        <IconButton
          onClick={handleNext}
          sx={{
            position: "absolute",
            right: -33,
            top: "40%",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
          aria-label="Próximo"
        >
          <ArrowForwardIosIcon />
        </IconButton>
      )}

      <Box
        mt={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={1}
      >
        {Array?.from?.({ length: maxIndex + 1 })?.map?.((_, idx) => (
          <Box
            onClick={() => setCurrent(idx)}
            key={idx}
            width={10}
            height={10}
            borderRadius="50%"
            bgcolor={idx === current ? "primary.main" : "grey.400"}
            sx={{
              cursor: "pointer",
              transition: "background 0.2s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
